import { createCriteriaParameters, standardDeleteQuery, standardGetQuery, standardPostQuery, standardPutQuery } from "./apiUtils.js";

import { replacePlaceholdersWithObjValues, setDeleteArrayState, setInsertArrayState } from "../shared/miscUtils";

import { SNACKBAR_SUCCESS_CREATED, SNACKBAR_SUCCESS_DELETED, SNACKBAR_SUCCESS_UPDATED } from "../../config/language/english";
import * as Constants from "../../config/constants/Constants.js";

/**
 * Function for querying resources with filters, pagination, sorting etc.
 */
export const queryResources = (props, options = {}) => {
    const path = Constants.API_RESOURCES + createCriteriaParameters(options);

    standardGetQuery(props, path, (response) => {
        const states = props.backend.resources;

        setInsertArrayState(states.setData, response.body.elements);
        states.setQueryLoading(false);

        if (options.pagination === false) return;

        if (options.page !== undefined) states.setQueriedUntil(options.page);
        if (response?.body?.total !== undefined) states.setRowCount(response.body.total);
    });
};

/**
 * Function for querying an existing resource.
 */
export const queryResource = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_RESOURCES_ID, { resource_id: options.id });

    standardGetQuery(props, path, (response) => {
        const states = props.backend.resources;

        setInsertArrayState(states.setData, [{ ...response.body }]);
    });
};

/**
 * Function for querying resources with filters, pagination, sorting etc.
 * NB! This is only meant for resource selection pop-up.
 */
export const querySelectResources = (props, states = {}, options = {}) => {
    const params = createCriteriaParameters(options);
    const path = Constants.API_RESOURCES + params;

    standardGetQuery(props, path, (response) => {
        setInsertArrayState(states.setData, response.body.elements);
    });
};

/**
 * Function for creating a new resource.
 */
export const createResource = (props, options = {}) => {
    const path = Constants.API_RESOURCES;

    standardPostQuery(props, path, options.body, (response) => {
        const states = props.backend.resources;

        setInsertArrayState(states.setData, [{ ...response.body }]);
        states.setRowCount((prev) => prev + 1);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_CREATED });
    });
};

/**
 * Function for updating a resource.
 */
export const updateResource = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_RESOURCES_ID, { resource_id: options.id });

    standardPutQuery(props, path, options.body, (response) => {
        const states = props.backend.resources;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for updating a resource.
 */
export const updateResources = (props, options = {}) => {
    standardPutQuery(props, Constants.API_RESOURCES, options.body, (response) => {
        const states = props.backend.resources;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for deleting resource(s).
 */
export const deleteResources = (props, options = {}) => {
    const path = Constants.API_RESOURCES;

    standardDeleteQuery(props, path, options.body, (response) => {
        const states = props.backend.resources;

        setDeleteArrayState(states.setData, options.ids);

        states.setRowCount(states.rowCount - options.ids.length);
        states.setSelected([]);

        // Delete all transcripts related to resources from states.
        const transcripts = props.backend.transcripts;
        const transcriptIds = transcripts.data.filter((t) => options.ids.includes(t.resource_id)).map((t) => t.id);

        setDeleteArrayState(transcripts.setData, transcriptIds);
        transcripts.setRowCount(Math.max(0, transcripts.rowCount - transcriptIds.length));
        transcripts.setSelected([]);

        // Delete all quotes related to transcripts from states.
        const quotes = props.backend.quotes;
        const quoteIds = quotes.data.filter((q) => transcriptIds.includes(q.transcript_id)).map((q) => q.id);

        setDeleteArrayState(quotes.setData, quoteIds);
        quotes.setRowCount(Math.max(0, quotes.rowCount - quoteIds.length));
        quotes.setSelected([]);

        // Delete all text translations related to resources from states.
        const textTranslations = props.backend.textTranslations;
        const textTranslationIds = textTranslations.data.filter((t) => options.ids.includes(t.resource_id)).map((t) => t.id);

        setDeleteArrayState(textTranslations.setData, textTranslationIds);
        textTranslations.setRowCount(Math.max(0, textTranslations.rowCount - textTranslationIds.length));
        textTranslations.setSelected([]);

        // Delete all audio translations from states.
        const audioTranslations = props.backend.audioTranslations;
        const audioTranslationIds = audioTranslations.data.filter((a) => textTranslationIds.includes(a.translation_id)).map((a) => a.id);

        setDeleteArrayState(audioTranslations.setData, audioTranslationIds);
        audioTranslations.setRowCount(Math.max(0, audioTranslations.rowCount - audioTranslationIds.length));
        audioTranslations.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_DELETED });
    });
};

/**
 * Function for approving a resource.
 */
export const approveResource = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_RESOURCES_ID_APPROVE, { resource_id: options.id });

    standardPutQuery(props, path, undefined, (response) => {
        const states = props.backend.resources;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for approving a resource.
 */
export const approveResources = (props, options = {}) => {
    standardPutQuery(props, Constants.API_RESOURCES_APPROVE, options.body, (response) => {
        const states = props.backend.resources;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for retrying a resource.
 */
export const retryResource = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_RESOURCES_ID_RETRY, { resource_id: options.id });

    standardPutQuery(props, path, undefined, (response) => {
        const states = props.backend.resources;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        // Delete all transcripts related to resources from states.
        const transcripts = props.backend.transcripts;
        const transcriptId = transcripts.data.filter((t) => options.id === t.resource_id).map((t) => t.id);

        setDeleteArrayState(transcripts.setData, transcriptId);
        transcripts.setRowCount(Math.max(0, transcripts.rowCount - transcriptId.length));
        transcripts.setSelected([]);

        // Delete all quotes related to transcripts from states.
        const quotes = props.backend.quotes;
        const quoteIds = quotes.data.filter((q) => transcriptId.includes(q.transcript_id)).map((q) => q.id);

        setDeleteArrayState(quotes.setData, quoteIds);
        quotes.setRowCount(Math.max(0, quotes.rowCount - quoteIds.length));
        quotes.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for retrying a resource.
 */
export const retryResources = (props, options = {}) => {
    standardPutQuery(props, Constants.API_RESOURCES_RETRY, options.body, (response) => {
        const states = props.backend.resources;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        // Delete all transcripts related to resources from states.
        const transcripts = props.backend.transcripts;
        const transcriptIds = transcripts.data.filter((t) => options.ids.includes(t.resource_id)).map((t) => t.id);

        setDeleteArrayState(transcripts.setData, transcriptIds);
        transcripts.setRowCount(Math.max(0, transcripts.rowCount - transcriptIds.length));
        transcripts.setSelected([]);

        // Delete all quotes related to transcripts from states.
        const quotes = props.backend.quotes;
        const quoteIds = quotes.data.filter((q) => transcriptIds.includes(q.transcript_id)).map((q) => q.id);

        setDeleteArrayState(quotes.setData, quoteIds);
        quotes.setRowCount(Math.max(0, quotes.rowCount - quoteIds.length));
        quotes.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for retrying a resource.
 */
export const retryResourceMetadata = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_RESOURCES_ID_RETRY_METADATA, { resource_id: options.id });

    standardPutQuery(props, path, undefined, (response) => {
        const states = props.backend.resources;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for retrying a resource.
 */
export const retryResourceMetadatas = (props, options = {}) => {
    standardPutQuery(props, Constants.API_RESOURCES_RETRY_METADATA, options.body, (response) => {
        const states = props.backend.resources;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};
